/**
 * Signing guard. The browser has no RPC of its own, so it trusts the cluster and genesis hash the Raydius server
 * reports in /api/config, and refuses to sign when the two disagree: mainnet's genesis under any other name, devnet's
 * under "local", testnet anywhere. Every signer asserts the verdict again right before it signs.
 */
import type { ApiCluster } from '@/api/types';

/** Genesis hashes of the public clusters. */
export const GENESIS = {
  mainnet: '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d',
  devnet: 'EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG',
  testnet: '4uhcVJyU9pJkvQyS88uRDiswHXSCkY3zQawwpjk2NsNY',
} as const;

/** Wallet Standard chains Privy signs for. */
export type SignChain = 'solana:mainnet' | 'solana:devnet';

export type RefusalCode = 'unchecked' | 'mismatch' | 'testnet' | 'public-cluster';

export type SigningVerdict =
  | { allowed: true; cluster: ApiCluster; genesisHash: string; chain: SignChain }
  | { allowed: false; code: RefusalCode; reason: string; genesisHash: string | null };

export interface ServerClusterClaim {
  cluster: ApiCluster;
  genesisHash: string;
}

const isPublic = (g: string) => (Object.values(GENESIS) as string[]).includes(g);

/**
 * - mainnet: the server must report mainnet's genesis; devnet: devnet's;
 * - local: a genesis that is none of the public clusters' (every test-validator clone has its own). Privy has no
 *   localnet chain id; signing is chain-agnostic, so a local clone signs as devnet;
 * - testnet's genesis is never accepted.
 */
export function signingVerdict(server: ServerClusterClaim | null | undefined): SigningVerdict {
  const g = server?.genesisHash ?? '';
  if (!server || !g) return { allowed: false, code: 'unchecked', reason: 'The Raydius server has not reported its cluster yet, so signing is off.', genesisHash: null };
  if (g === GENESIS.testnet) return { allowed: false, code: 'testnet', reason: 'The Raydius server reports Solana testnet, which Raydius does not use.', genesisHash: g };
  if (server.cluster === 'mainnet') {
    return g === GENESIS.mainnet
      ? { allowed: true, cluster: 'mainnet', genesisHash: g, chain: 'solana:mainnet' }
      : { allowed: false, code: 'mismatch', reason: "The server says mainnet, but its genesis hash is not mainnet's.", genesisHash: g };
  }
  if (server.cluster === 'devnet') {
    return g === GENESIS.devnet
      ? { allowed: true, cluster: 'devnet', genesisHash: g, chain: 'solana:devnet' }
      : { allowed: false, code: 'mismatch', reason: "The server says devnet, but its genesis hash is not devnet's.", genesisHash: g };
  }
  if (isPublic(g)) return { allowed: false, code: 'public-cluster', reason: 'The server says local, but its genesis hash belongs to a public cluster.', genesisHash: g };
  return { allowed: true, cluster: 'local', genesisHash: g, chain: 'solana:devnet' };
}

export class SigningRefusedError extends Error {
  readonly code: RefusalCode;
  constructor(code: RefusalCode, reason: string) {
    super(reason);
    this.name = 'SigningRefusedError';
    this.code = code;
  }
}

export function assertSigningAllowed(v: SigningVerdict): asserts v is Extract<SigningVerdict, { allowed: true }> {
  if (!v.allowed) throw new SigningRefusedError(v.code, v.reason);
}
